import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationOptions, UndefinedInitialDataOptions } from '@tanstack/react-query';
import { useCheckAccess } from 'modules/accessControl/components/AccessControl';
import type { ApiResponse } from 'apisauce';
import { t } from '@lingui/macro';
import type { PaginateResponse } from 'types';
import { adapter, adapterPaged } from '../utils';
import { DefaultError } from '@tanstack/query-core';
import { set } from 'lodash/fp';
import { size, forEach } from 'lodash';
import { useUrlState } from 'modules/urlState/hooks/useUrlState';
import { useNotification } from 'modules/notifications/hooks';
import { USE_FEDERATED_AUTH } from 'config';
import { useAuthState, useSelectedOrg } from 'features/auth/hooks/federatedAuthHooks';

type Ids = {
  id: string | number;
};

export type Url =
  | ((params: {
      query?: URLSearchParams;
      vars?: string[];
      data?: unknown;
    }) => Promise<ApiResponse<unknown, unknown>>)
  | null;

const noAccessData = {
  Error: t`No Access`,
  Message: t`You do not have write access to the requested page.`,
};
const noAccessPromise = () =>
  Promise.resolve({
    ok: true,
    data: noAccessData,
  } as ApiResponse<typeof noAccessData>);

/**
 * @param url - The url to fetch data from
 * @param key - queryKey forwarded to useQuery
 * @param vars - An array of variables to be used in the url
 * @param transform - A function to transform the data before returning it
 * @param revalidate - By default we don't cache data, setting this to false will turn on chaching
 * @param options - Query options forwarded to useQuery
 * @param allowAnonymous - Set for PUBLIC endpoints (e.g. feature flags) that must load
 *   without a login: skips the federated-auth gate so the query also runs for
 *   unauthenticated visitors on public routes like /supplier/signup.
 * @returns a useQuery hook
 */
export function useGet<T>(
  url: Url,
  key: string | readonly string[],
  vars: string[] = [],
  transform?: (data: T) => T,
  revalidate: boolean = true,
  options: Omit<UndefinedInitialDataOptions<T>, 'queryKey' | 'queryFn' | 'staleTime'> = {},
  allowAnonymous: boolean = false
) {
  const { notify } = useNotification();
  const { isAuthenticated, isLoading } = useAuthState();
  const { orgToken } = useSelectedOrg();

  // Under federated auth, the request-layer Authorization header is only org-scoped
  // once `orgToken` is set (see AuthProviderFederated.tsx / OrgProvider); gating on the
  // bare Auth0 token alone lets requests fire with a missing/wrong-scoped token and 401.
  const isApiAuthenticated =
    USE_FEDERATED_AUTH && !allowAnonymous
      ? isAuthenticated && !isLoading && Boolean(orgToken)
      : true;
  // Never fire a request whose URL isn't fully resolved. During a company switch the user
  // query is cleared, so org-scoped vars (e.g. `String(orgId)`) briefly stringify to the
  // literal "undefined" and the request goes to `…/organizations/undefined/…` → the backend
  // rejects it with "Invalid organization ID". Gating here covers every org-scoped query in
  // both auth modes (safer than `Boolean(orgId)`, which also blocks a legitimate `0`).
  const hasUnresolvedVar = vars.some(v => v == null || v === 'undefined' || v === 'null');
  const enabled = isApiAuthenticated && !hasUnresolvedVar && (options.enabled ?? true);

  return useQuery<T>({
    ...options,
    queryKey: key ? (typeof key === 'string' ? [key] : key) : [''],
    queryFn: url ? () => adapter(url({ vars }), transform, notify.error) : undefined,
    ...(revalidate ? { staleTime: 0 } : {}),
    enabled,
  });
}

type PageState = {
  index: number;
  cursors: string[];
};

// stable empty-array reference returned while a paged query has no data yet
const EMPTY_PAGE: [] = [];

/**
 * Cursor-based pagination wrapper around useQuery.
 *
 * @param url - The url to fetch data from
 * @param key - queryKey array forwarded to useQuery
 * @param query - URL query parameters for the first page; subsequent pages use cursor only
 * @param vars - Variables passed to the url builder
 * @param transform - Transforms each page of data before returning
 * @param revalidate - When true, disables caching (staleTime: 0)
 * @param options - Query options forwarded to useQuery
 * @param pageSize - Reported page size for consumers (e.g. table UI); defaults to 20
 * @returns useQuery result plus pagination helpers and `pageSize`
 */
export function usePagedGet<T, E extends object = Record<never, never>>(
  url: Url,
  key: string[],
  query: URLSearchParams = new URLSearchParams(),
  vars: string[] = [],
  transform?: (data: T[]) => T[],
  revalidate: boolean = false,
  options: Omit<UndefinedInitialDataOptions<PaginateResponse<T>>, 'queryKey'> = {},
  pageSize = 20
) {
  const { notify } = useNotification();
  const [urlState, setUrlState] = useUrlState<{ page: string | null; cursor: string | null }>(
    `${String(key?.[0])}-use-paged-get`,
    { page: null, cursor: null },
    false
  );
  const [page, setPage] = useState<PageState>({
    index: urlState.page ? parseInt(urlState.page) - 1 : 0,
    cursors: urlState.cursor ? ['', urlState.cursor] : [''],
  });
  let params = query;
  if (page.cursors[page.index]) {
    params = new URLSearchParams();
    params.append('cursor', page.cursors[page.index]);
  }
  useEffect(() => {
    // if urlState has been reset from outside, reset page state
    if ((!urlState.page || !urlState.cursor) && page.index !== 0) {
      setPage({ index: 0, cursors: [''] });
    }
  }, [urlState]);
  const { token } = useAuthState();
  const { orgToken } = useSelectedOrg();
  // See useGet above: gate on the org-scoped token, not just the bare Auth0 token.
  const isApiAuthenticated = USE_FEDERATED_AUTH ? Boolean(token) && Boolean(orgToken) : true;
  // See useGet: skip the request while any url var is unresolved (stringifies to "undefined"
  // during the ~1s company-switch window) so we never call `…/organizations/undefined/…`.
  const hasUnresolvedVar = vars.some(v => v == null || v === 'undefined' || v === 'null');
  const pagedQuery = useQuery<PaginateResponse<T>>({
    ...options,
    queryKey: [...key, page.cursors[page.index]],
    queryFn: url
      ? () => adapterPaged(url({ query: params, vars }), transform, notify.error)
      : undefined,
    ...(revalidate ? { staleTime: 0 } : {}),
    refetchOnMount: 'always',
    // don't fetch if there's a mismatch between urlState and page state, this will be fixed in the useEffect above
    enabled:
      isApiAuthenticated &&
      !hasUnresolvedVar &&
      !((!urlState.page || !urlState.cursor) && page.index !== 0) &&
      (options?.enabled ?? true),
  });
  const nextPage = (cursor: string, statePageIndex: number) => {
    setPage(prev => {
      const newCursors = [...prev.cursors];
      newCursors[prev.index + 1] = cursor; // insert or replace at exact index
      return {
        index: prev.index + 1,
        cursors: newCursors,
      };
    });
    setUrlState({ page: (statePageIndex + 1).toString(), cursor });
  };
  const previousPage = (statePageIndex: number) => {
    setPage(prev => ({
      index: prev.index - 1,
      cursors: prev.cursors.slice(0, statePageIndex + 1),
    }));
    if (statePageIndex === 0) {
      setUrlState({ page: null, cursor: null });
      return;
    }
    setUrlState({ page: (statePageIndex + 1).toString(), cursor: page.cursors[statePageIndex] });
  };
  const changePage = (statePageIndex: number) => {
    if (statePageIndex === 0) {
      setPage(prev => ({ index: 0, cursors: prev.cursors }));
      setUrlState({ page: null, cursor: null });
      return;
    }
    if (statePageIndex > page.index) {
      nextPage(pagedQuery.data?.pagination.cursor || '', statePageIndex);
    }
    if (statePageIndex < page.index) {
      previousPage(statePageIndex);
    }
  };
  return {
    ...pagedQuery,
    data: pagedQuery.data?.data ?? EMPTY_PAGE,
    pagination: pagedQuery.data?.pagination || { hasMore: false, cursor: '' },
    // Raw page envelope as served — the only place endpoint-specific TOP-LEVEL fields riding
    // next to `data`/`pagination` (e.g. style-items' `inactiveLinkedItemCount`) survive. `E`
    // declares those extras; they widen the envelope as ALL-OPTIONAL props, so the served
    // superset always satisfies the asserted type — but TS can't verify that for an open
    // generic, hence the assertion.
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- safe all-optional widening
    response: pagedQuery.data as (PaginateResponse<T> & Partial<E>) | undefined,
    pageIndex: page.index,
    pageSize,
    nextPage,
    previousPage,
    changePage,
    queryKey: [...key, page.cursors[page.index]],
  };
}

type Post<T> = {
  data: Partial<T> | Partial<T>[];
  queryKey?: string[];
  query?: URLSearchParams;
  urlParams?: string[];
};

/**
 * @param url - The url to post data to
 * @param updateCache - automatically update data in queryCache with response
 * @param vars - variables used in url
 * @param options - Mutation options forwarded to useMutation
 * @returns a useMutation hook
 */
export function usePost<T extends Ids>(
  url: Url,
  updateCache: boolean = false,
  vars: string[] = [],
  options: UseMutationOptions<unknown, DefaultError, Post<T>, unknown> = {}
) {
  const { notify } = useNotification();
  const queryClient = useQueryClient();
  const hasAccess = useCheckAccess(true);

  let cacheUpdater: Partial<UseMutationOptions<unknown, DefaultError, Post<T>, unknown>> = {};
  if (updateCache) {
    cacheUpdater = {
      onSuccess: (data, variables, context) => {
        options?.onSuccess?.(data, variables, context);
        const { queryKey } = variables;
        if (!queryKey) {
          return;
        }
        const prevData = queryClient.getQueryData(queryKey) as PaginateResponse<T>;
        const responseData = data as { data: { data: T } };
        const newDataIndex = prevData.data.findIndex(d => Number(d.id) < 0);
        const newData = set(`[${newDataIndex}]`, responseData.data, prevData.data);
        queryClient.setQueryData(queryKey, { ...prevData, data: newData });
      },
      onError: (err, newData, context) => {
        options?.onError?.(err, newData, context);
        const ctx = context as Context<T>;
        queryClient.setQueryData(ctx.queryKey, ctx.prevData);
      },
    };
  }

  return useMutation<unknown, DefaultError, Post<T>, unknown>({
    ...options,
    mutationFn:
      hasAccess && url
        ? props =>
            adapter(
              url({ data: props.data, vars: props.urlParams ?? vars, query: props.query }),
              undefined,
              notify.error
            )
        : () => noAccessPromise(),
    ...cacheUpdater,
  });
}

export type Put<T> = {
  data: Partial<T> | Partial<T>[];
  tableUpdate?: Record<string, unknown>;
  index?: number;
  rows?: number[];
  urlParams?: string[];
  queryKey?: string[];
  /**
   * When true, the automatic error toast is suppressed so the caller can surface
   * the server message itself (e.g. inline cell validation). The mutation still
   * rejects, so `mutateAsync` callers can catch and display the error inline.
   */
  suppressErrorToast?: boolean;
};

type Context<T> = {
  prevData: PaginateResponse<T>;
  newData: PaginateResponse<T>;
  queryKey: string[];
};

/**
 * @param url - The url to post data to
 * @param updateCache - optimistically update the cache with the new data, requires columnId, value, index, queryKey in mutate function
 * @param options - Mutation options forwarded to useMutation
 * @returns a useMutation hook
 */
export function usePut<T, C = T>(
  url: Url,
  updateCache: boolean = false,
  options: UseMutationOptions<unknown, DefaultError, Put<T>, unknown> = {}
) {
  const { notify } = useNotification();
  const queryClient = useQueryClient();
  const hasAccess = useCheckAccess(true);
  let cacheUpdater: Partial<UseMutationOptions<unknown, DefaultError, Put<T>, unknown>> = {};
  if (updateCache) {
    cacheUpdater = {
      onMutate: ({ tableUpdate, index, rows, queryKey }) => {
        if (!queryKey) {
          return;
        }
        const prevData = queryClient.getQueryData(queryKey) as PaginateResponse<C>;
        const newData = {
          ...prevData,
          data: prevData.data.map((obj: C, objIndex: number) => {
            if ((objIndex === index || rows?.includes(objIndex)) && size(tableUpdate) > 0) {
              let newObj = { ...obj } as object;
              forEach(tableUpdate, (value, columnId) => {
                newObj = set(columnId, value, newObj);
              });
              return newObj;
            }
            return obj;
          }),
        };
        queryClient.setQueryData(Array.isArray(queryKey) ? queryKey : [queryKey], newData);
        return { newData, prevData, queryKey };
      },
      onError: (err, newData, context) => {
        const ctx = context as Context<C> | undefined;
        if (ctx?.queryKey) {
          queryClient.setQueryData(ctx.queryKey, ctx.prevData);
        }
      },
    };
  }
  return useMutation<unknown, DefaultError, Put<T>, unknown>({
    ...options,
    mutationFn:
      hasAccess && url
        ? props => {
            return adapter(
              url({ data: props.data, vars: props.urlParams }),
              undefined,
              props.suppressErrorToast ? undefined : notify.error
            );
          }
        : () => noAccessPromise(),
    ...cacheUpdater,
  });
}

export type Delete = {
  ids: { id: string | number }[];
  queryKey?: string[];
  /** When set, replaces hook-level path vars (cert id is still appended from ids). */
  urlParams?: string[];
};

/**
 * @param url - The url to post data to
 * @param options - Mutation options forwarded to useMutation
 * @returns a useMutation hook
 */
export function useDelete<T extends Ids>(
  url: Url,
  updateCache: boolean = false,
  vars: string[] = [],
  options: UseMutationOptions<ApiResponse<unknown>, Error, Delete, unknown> = {}
) {
  const { notify } = useNotification();
  const queryClient = useQueryClient();
  const hasAccess = useCheckAccess(true);

  const deleteSingle = async (id: string, baseVars: string[] = vars) => {
    if (!hasAccess || !url) return noAccessPromise();
    return await adapter(url({ vars: [...baseVars, id] }), undefined, notify.error);
  };

  const deleteBatch = async (ids: string[] = [], baseVars: string[] = vars) => {
    if (!hasAccess || !url) return noAccessPromise();
    return await adapter(url({ data: ids, vars: baseVars }), undefined, notify.error);
  };

  let cacheUpdater: Partial<UseMutationOptions<ApiResponse<unknown>, Error, Delete, unknown>> = {};
  if (updateCache) {
    cacheUpdater = {
      onMutate: ({ ids, queryKey }) => {
        if (!queryKey) {
          return;
        }
        const prevData = queryClient.getQueryData<PaginateResponse<T>>(queryKey);
        if (!prevData) return;
        queryClient.setQueryData(queryKey, {
          ...prevData,
          data: prevData.data.filter(
            task => !ids.find(id => id.id.toString() === task.id.toString())
          ),
        });
      },
      onError: (err, newData, context) => {
        const ctx = context as Context<T>;
        queryClient.setQueryData(ctx.queryKey, ctx.prevData);
      },
    };
  }

  return useMutation({
    ...options,
    mutationFn:
      hasAccess && url
        ? async props => {
            const baseVars = props.urlParams ?? vars;
            return (
              props.ids.length > 1
                ? await deleteBatch(
                    props.ids.map(id => id.id.toString()),
                    baseVars
                  )
                : await deleteSingle(props.ids[0].id.toString(), baseVars)
            ) as ApiResponse<unknown>;
          }
        : async () => (await noAccessPromise()) as ApiResponse<typeof noAccessData>,
    ...cacheUpdater,
  });
}

/**
 * @param get - The url to fetch data from
 * @param key - queryKey forwarded to useQuery
 * @param put - The url to put data to
 * @param post - The url to post data to
 * @param remove - The url to delete data from
 * @param revalidate - By default we cache data, setting this to true will always refetch the data
 * @returns an object of hooks { get, create, update, remove }
 */
export const useApi = (
  get: Url,
  key: string,
  put: Url,
  post: Url,
  remove: Url,
  revalidate: boolean = false
) => {
  return {
    get: useGet(get, key, [], undefined, revalidate),
    create: usePut(put),
    update: usePost(post),
    remove: useDelete(remove),
  };
};

interface UseAddItemBeforeSaveOptions<T> {
  queryKey: string | string[]; // The key for querying/updating the list in the queryClient
  defaultItem: T; // The default item to be inserted, of generic type T
}

export const useAddItemBeforeSave = <T,>({
  queryKey,
  defaultItem,
}: UseAddItemBeforeSaveOptions<T>) => {
  const queryClient = useQueryClient();

  const addItem = () => {
    const prevData = queryClient.getQueryData<PaginateResponse<T>>(
      Array.isArray(queryKey) ? queryKey : [queryKey]
    );

    if (!prevData) return;

    const newData = {
      ...prevData,
      data: [
        ...prevData.data,
        defaultItem, // Insert the default item (of generic type T)
      ],
    };

    queryClient.setQueryData(Array.isArray(queryKey) ? queryKey : [queryKey], newData);
  };

  return { addItem };
};

export const useGetCache = <T,>(key: string | string[]) => {
  const queryClient = useQueryClient();
  const getCached = () => {
    const response = queryClient.getQueryData<{ data: T }>(Array.isArray(key) ? key : [key]);
    return response?.data;
  };
  return getCached;
};

export const useCache = <T,>(key: string | string[]) => {
  const { data } = useQuery<{ data: T }>({
    queryKey: Array.isArray(key) ? key : [key],
    enabled: false,
  });
  return data?.data;
};
